import { useEffect, useState } from 'react';
import apiClient from '../services/apiClient';

/**
 * S76 — ONE shared source for GET /api/messages/unread-summary.
 *
 * Before this, AISupportAgent and MessagesWaitingCard each fetched the endpoint
 * independently, and each re-fetched on their own triggers (a per-card
 * refreshKey, plus every `mtl:client-message-*` window event dispatched by
 * several components during load) — so one page load hit the endpoint 7+ times.
 *
 * This module holds a single in-memory store: it fetches ONCE on first mount,
 * refreshes on ONE 30s interval, and coalesces every event-driven refetch
 * through a 400ms debounce + in-flight dedupe. Every consumer reads the same
 * result. It does not change what the count shows, only how often it is fetched.
 */
let _state = { events: [], totalUnseen: 0, total: 0, raw: null, loaded: false };
const _subs = new Set();
let _inflight = null;
let _interval = null;
let _debounce = null;
let _teardown = null;

function _emit() {
  for (const fn of _subs) fn(_state);
}

function _fetch() {
  if (_inflight) return _inflight; // dedupe concurrent/rapid callers
  _inflight = apiClient
    .get('/messages/unread-summary')
    .then((res) => {
      const d = res.data || {};
      _state = {
        events: d.events || [],
        totalUnseen: Number(d.totalUnseen || 0),
        total: Number(d.total || 0),
        raw: d,
        loaded: true,
      };
      _emit();
    })
    .catch(() => { /* silent — a failed poll must never break the shell */ })
    .finally(() => { _inflight = null; });
  return _inflight;
}

function _scheduleRefresh() {
  clearTimeout(_debounce);
  _debounce = setTimeout(() => { _fetch(); }, 400);
}

/** Ask the single source to refresh (debounced + deduped). Safe to call often. */
export function refreshUnreadSummary() {
  _scheduleRefresh();
}

export default function useUnreadSummary() {
  const [state, setState] = useState(_state);

  useEffect(() => {
    _subs.add(setState);

    // First consumer: fetch once and start the single interval + listeners.
    if (_subs.size === 1) {
      if (!_state.loaded && !_inflight) _fetch();
      _interval = setInterval(() => { _fetch(); }, 30000);
      const onChange = () => _scheduleRefresh();
      window.addEventListener('mtl:client-message-new', onChange);
      window.addEventListener('mtl:client-message-handled', onChange);
      _teardown = () => {
        clearInterval(_interval);
        _interval = null;
        clearTimeout(_debounce);
        window.removeEventListener('mtl:client-message-new', onChange);
        window.removeEventListener('mtl:client-message-handled', onChange);
      };
    } else if (!_state.loaded && !_inflight) {
      // A later consumer mounted before the first load resolved.
      _fetch();
    }

    return () => {
      _subs.delete(setState);
      if (_subs.size === 0 && _teardown) {
        _teardown();
        _teardown = null;
      }
    };
  }, []);

  return { ...state, refresh: _scheduleRefresh };
}
